import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../api/client';

// ── Design tokens (from fleet globals.css) ──────────────────────────────────
const T = {
  bgBase: '#F7F5F0',
  bgSunken: '#EEEBE4',
  bgHover: '#EAE6DD',
  border: 'rgba(15,14,12,0.07)',
  borderStrong: 'rgba(15,14,12,0.14)',
  text: '#0F0E0C',
  textMuted: '#3A3830',
  textDim: '#7A7770',
  brand: '#00955F',
  brand600: '#007A4D',
  brand50: '#E0F1E8',
  brandRing: 'rgba(0,149,95,0.24)',
  danger: '#D32020',
  danger50: '#FADEDE',
  darkPanel: '#0A2E1F',
  radius: '12px',
  radiusSm: '8px',
  font: "'Nunito', ui-sans-serif, system-ui, sans-serif",
};

// ── LogoMark SVG (satellite icon from fleet brand/logo.tsx) ─────────────────
function LogoMark({ size = 40, variant = 'light' as 'light' | 'dark' }: { size?: number; variant?: 'light' | 'dark' }) {
  const isDark = variant === 'dark';
  const panelFrom = isDark ? '#34D399' : '#0A2E1F';
  const panelTo   = isDark ? '#10B981' : '#1A5039';
  const bodyFrom  = isDark ? '#F0FDF4' : '#EDF2F6';
  const bodyMid   = isDark ? '#D1FAE5' : '#C6CFD8';
  const bodyTo    = isDark ? '#A7F3D0' : '#8A95A3';
  const centerStroke = isDark ? '#A7F3D0' : '#0A2E1F';
  const uid = isDark ? 'dark' : 'light';
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id={`pa-body-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={bodyFrom} />
          <stop offset="50%" stopColor={bodyMid} />
          <stop offset="100%" stopColor={bodyTo} />
        </linearGradient>
        <linearGradient id={`pa-panel-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={panelFrom} />
          <stop offset="100%" stopColor={panelTo} />
        </linearGradient>
        <linearGradient id="pa-ring" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#00A36C" stopOpacity="0" />
          <stop offset="50%" stopColor="#00A36C" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#58D8E8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <ellipse cx="32" cy="32" rx="27" ry="10.5" stroke="url(#pa-ring)" strokeWidth="1.4" transform="rotate(-20 32 32)" />
      <g transform="rotate(20 32 32)">
        <rect x="5"  y="27" width="15" height="10" rx="1.5" fill={`url(#pa-panel-${uid})`} />
        <line x1="9"    y1="27" x2="9"    y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="12.5" y1="27" x2="12.5" y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="16"   y1="27" x2="16"   y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <rect x="44" y="27" width="15" height="10" rx="1.5" fill={`url(#pa-panel-${uid})`} />
        <line x1="48"   y1="27" x2="48"   y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="51.5" y1="27" x2="51.5" y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="55"   y1="27" x2="55"   y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="20" y1="32" x2="24" y2="32" stroke={centerStroke} strokeWidth="1.6" />
        <line x1="40" y1="32" x2="44" y2="32" stroke={centerStroke} strokeWidth="1.6" />
        <rect x="23" y="25" width="18" height="14" rx="3" fill={`url(#pa-body-${uid})`} stroke="#64748B" strokeWidth="0.4" />
        <rect x="25" y="27" width="14" height="10" rx="1.5" fill={isDark ? '#064E3B' : '#0A2E1F'} fillOpacity="0.22" />
        <circle cx="32" cy="32" r="2.6" fill="#FFFFFF" />
        <circle cx="32" cy="32" r="1.2" fill="#00A36C" />
      </g>
      <g>
        <path d="M 44 16 Q 49 19 51 24" stroke="#00A36C" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.9" />
        <path d="M 47 12 Q 54 16 56 24" stroke="#00A36C" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.55" />
      </g>
    </svg>
  );
}

// ── Stars / Constellations background (from fleet login-scene.tsx) ───────────
const STARS = [
  [8,12],[15,28],[22,9],[30,44],[38,18],[47,34],[55,11],[62,52],
  [70,24],[78,40],[85,8],[92,30],[12,60],[20,78],[28,92],[35,65],
  [43,84],[50,72],[58,95],[66,68],[74,88],[82,74],[90,94],[96,60],
  [5,40],[18,50],[25,20],[33,75],[42,55],[52,40],[60,80],[68,15],
  [76,55],[86,45],[94,20],[14,88],[36,35],[56,62],[72,5],[88,78],
] as const;

const CONSTELLATIONS: [number,number,number,number][] = [
  [15,28,22,9],[22,9,38,18],[38,18,47,34],[47,34,55,11],
  [28,92,35,65],[35,65,43,84],[66,68,74,88],[74,88,82,74],
];

// Dots for globe (from fleet login page.tsx)
const GLOBE_DOTS: [number,number][] = [
  [-190,-130],[-175,-155],[-155,-175],[-130,-160],[-110,-145],[-90,-150],
  [-70,-165],[-50,-160],[-30,-145],[-10,-130],[-145,-120],[-125,-110],
  [-105,-100],[-85,-95],[-65,-90],[-45,-85],[-145,-85],[-120,-75],
  [-90,-70],[-70,-60],[-115,-55],[-95,-45],[-80,-30],[-65,-15],
  [-55,5],[-45,30],[-35,55],[-30,80],[-25,105],[-20,130],
  [-15,155],[-10,180],[-5,200],[5,210],[15,205],[25,190],
  [35,170],[45,140],[50,110],[55,80],[55,50],[50,20],
  [45,-5],[35,-25],[25,-40],[15,-50],[-10,-45],
  [60,-180],[85,-180],[110,-175],[135,-170],[160,-160],[185,-150],
  [210,-140],[45,-160],[70,-155],[95,-150],[120,-145],[145,-140],
  [170,-130],[195,-125],[70,-120],[95,-118],[120,-115],[145,-110],
  [170,-100],[60,-90],[85,-85],[110,-78],[135,-65],[150,-45],
  [155,-20],[150,5],[140,30],[125,55],[105,75],[85,90],
  [65,95],[50,105],[60,130],[75,150],[85,170],[75,120],
  [95,130],[115,125],[135,115],[155,100],[175,85],[195,70],
  [215,55],[235,40],[90,-175],[115,-170],[140,-175],[165,-175],
  [190,-170],[215,-160],[235,-145],[250,-125],[255,-100],[250,-70],
  [230,-50],[205,-40],[180,-35],[155,-40],[185,105],[205,110],
  [225,115],[240,125],[245,140],[230,150],[210,152],[190,148],
  [175,135],[175,120],[-70,-200],[-55,-205],[-40,-200],[-30,-190],
  [-35,-180],[-55,-180],[-70,-190],
];

function LeftPanel() {
  return (
    <aside className="hidden lg:flex relative overflow-hidden text-white" style={{ background: T.darkPanel }}>
      {/* LoginScene radial gradients */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(1200px 700px at 85% 10%, rgba(0,163,108,0.35), transparent 55%), radial-gradient(900px 600px at 10% 95%, rgba(0,120,168,0.28), transparent 55%), radial-gradient(400px 400px at 50% 50%, rgba(88,216,232,0.08), transparent 70%)',
        }}
      />
      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.05,
          backgroundImage: 'linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      {/* Stars + Constellations */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        <g fill="#ffffff">
          {STARS.map(([x,y], i) => (
            <circle key={i} cx={x} cy={y} r={(i%5)*0.03+0.08} opacity={0.35+(i%4)*0.15}>
              {i%6===0 ? <animate attributeName="opacity" values="0.3;0.9;0.3" dur={`${3+(i%4)}s`} repeatCount="indefinite" /> : null}
            </circle>
          ))}
        </g>
        <g stroke="#ffffff" strokeWidth="0.05" strokeOpacity="0.15">
          {CONSTELLATIONS.map(([x1,y1,x2,y2], i) => <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />)}
        </g>
      </svg>
      {/* Noise */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='.12'/%3E%3C/svg%3E\")",
        }}
      />
      {/* Overlay noise from login page */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='320'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='320' height='320' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E\")",
          opacity: 0.08,
          mixBlendMode: 'overlay',
        }}
      />

      {/* Globe SVG (3D tilted) */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ perspective: '1800px' }}>
        <svg
          viewBox="-300 -300 600 600"
          width="140%"
          height="140%"
          fill="none"
          style={{ opacity: 0.5, transform: 'rotateX(22deg) rotateZ(-16deg)', transformStyle: 'preserve-3d' }}
        >
          <defs>
            <radialGradient id="globe-fade" cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#00A36C" stopOpacity="0.15" />
              <stop offset="65%"  stopColor="#00A36C" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#00A36C" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="globe-stroke" x1="0" y1="-1" x2="0" y2="1">
              <stop offset="0%"   stopColor="#58D8E8" stopOpacity="0.35" />
              <stop offset="50%"  stopColor="#00A36C" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#58D8E8" stopOpacity="0.35" />
            </linearGradient>
            <filter id="globe-glow">
              <feGaussianBlur stdDeviation="1.4" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <clipPath id="globe-clip"><circle cx="0" cy="0" r="238" /></clipPath>
          </defs>
          <circle cx="0" cy="0" r="240" fill="url(#globe-fade)" />
          <g stroke="url(#globe-stroke)" fill="none" strokeWidth="0.7">
            <circle cx="0" cy="0" r="240" strokeOpacity="0.32" />
            <ellipse cx="0" cy="0" rx="240" ry="60"  strokeOpacity="0.12" />
            <ellipse cx="0" cy="0" rx="240" ry="130" strokeOpacity="0.12" />
            <ellipse cx="0" cy="0" rx="240" ry="195" strokeOpacity="0.1"  />
            <ellipse cx="0" cy="0" rx="60"  ry="240" strokeOpacity="0.12" />
            <ellipse cx="0" cy="0" rx="130" ry="240" strokeOpacity="0.12" />
            <ellipse cx="0" cy="0" rx="195" ry="240" strokeOpacity="0.1"  />
          </g>
          <g clipPath="url(#globe-clip)" fill="#00A36C">
            {GLOBE_DOTS.map(([x,y], i) => (
              <circle key={i} cx={x} cy={y} r="1.6" fillOpacity={0.7+((i%5)*0.05)} />
            ))}
          </g>
          <g stroke="#00A36C" strokeDasharray="2 4" fill="none" strokeWidth="0.7" opacity="0.35">
            <path d="M -20,60 Q -100,-10 -180,-60" />
            <path d="M -20,60 Q 100,-20 210,-100" />
            <path d="M -20,60 Q 80,180 200,160" />
          </g>
          <g>
            <circle cx="-20" cy="60" r="4.5" fill="#00A36C">
              <animate attributeName="r" values="3;7;3" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="1;0.4;1" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle cx="-20" cy="60" r="2.5" fill="#ffffff" />
          </g>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center w-full pointer-events-none" style={{ padding: '64px' }}>
        <div style={{ maxWidth: '36rem' }}>
          <div style={{ marginBottom: '32px', marginLeft: '-8px' }}>
            <LogoMark size={168} variant="light" />
          </div>
          <h1
            className="font-bold text-white"
            style={{ fontFamily: T.font, fontSize: 'clamp(42px,4vw,62px)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            Toda a frota,<br />
            <span style={{ color: T.brand }}>em uma órbita.</span>
          </h1>
          <p style={{ marginTop: '16px', fontSize: '18px', lineHeight: 1.65, maxWidth: '38ch', color: 'rgba(255,255,255,0.65)', fontFamily: T.font }}>
            Acompanhe posição, rota e comportamento de cada veículo em tempo real.
          </p>
          <ul style={{ marginTop: '32px', display: 'grid', gap: '12px', maxWidth: '36ch' }}>
            {[
              { path: 'M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856a9.75 9.75 0 0113.788 0M1.924 8.674a14.25 14.25 0 0120.152 0M12 20.25h.008v.008H12v-.008z', label: 'Rastreamento em tempo real' },
              { path: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0', label: 'Alertas instantâneos via WhatsApp' },
              { path: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Histórico e replay de 90 dias' },
            ].map(({ path, label }) => (
              <li key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontFamily: T.font }}>
                <span style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: `1px solid ${T.brand}4D`, background: `${T.brand}1A`, color: T.brand, flexShrink: 0 }}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                    <path d={path} />
                  </svg>
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

// ── Right panel — Login form ─────────────────────────────────────────────────
export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  useEffect(() => {
    if (!lockedUntil) return;
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, [lockedUntil]);

  const lockedRemaining = lockedUntil ? Math.max(0, Math.ceil((lockedUntil - now) / 1000)) : 0;
  const isLocked = lockedRemaining > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isLocked) return;
    setIsLoading(true);
    setError(null);
    try {
      await login(email, password);
      navigate('/map', { replace: true });
    } catch (err) {
      const nextFails = failures + 1;
      setFailures(nextFails);
      if (nextFails >= 3) setLockedUntil(Date.now() + 30_000);
      if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
        setError('E-mail ou senha inválidos.');
      } else {
        setError('Erro ao conectar. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    border: `1px solid ${T.borderStrong}`,
    borderRadius: T.radiusSm,
    background: T.bgBase,
    color: T.text,
    fontSize: '14px',
    fontFamily: T.font,
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box' as const,
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 440px', fontFamily: T.font }}>
      <LeftPanel />

      <main
        className="flex flex-col"
        style={{ background: T.bgBase, padding: '24px 48px', position: 'relative' }}
      >
        {/* Mobile logo */}
        <div className="lg:hidden flex justify-center" style={{ marginBottom: '32px' }}>
          <LogoMark size={72} variant="dark" />
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: '24rem' }}>
            <h2
              className="font-bold"
              style={{ color: T.text, fontSize: '31.25px', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '32px' }}
            >
              Bem-vindo de volta
            </h2>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Error */}
              <div role="alert" aria-live="assertive" aria-atomic="true">
                {error && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', borderRadius: T.radiusSm, border: `1px solid ${T.danger}40`, background: T.danger50, padding: '12px 16px' }}>
                    <svg style={{ width: 16, height: 16, marginTop: 1, color: T.danger, flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <div style={{ fontSize: '14px', color: T.danger, fontWeight: 600 }}>{error}</div>
                      {isLocked && (
                        <div style={{ fontSize: '12px', color: T.danger, opacity: 0.8, marginTop: 4 }}>
                          Aguarde {lockedRemaining}s para tentar novamente.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Email */}
              <label style={{ display: 'block' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: T.text, marginBottom: '8px' }}>E-mail</div>
                <input
                  ref={emailRef}
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="voce@primeair.com.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = T.brand; e.target.style.boxShadow = `0 0 0 3px ${T.brandRing}`; }}
                  onBlur={(e)  => { e.target.style.borderColor = T.borderStrong; e.target.style.boxShadow = 'none'; }}
                />
              </label>

              {/* Password */}
              <label style={{ display: 'block' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', fontWeight: 600, color: T.text, marginBottom: '8px' }}>
                  <span>Senha</span>
                  {capsLock && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '12px', color: '#E07B00', fontWeight: 600 }}>
                      <svg style={{ width: 12, height: 12 }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Caps Lock ativo
                    </span>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyUp={(e) => setCapsLock(e.getModifierState?.('CapsLock') ?? false)}
                    onKeyDown={(e) => setCapsLock(e.getModifierState?.('CapsLock') ?? false)}
                    disabled={isLoading}
                    style={{ ...inputStyle, paddingRight: '40px' }}
                    onFocus={(e) => { e.target.style.borderColor = T.brand; e.target.style.boxShadow = `0 0 0 3px ${T.brandRing}`; }}
                    onBlur={(e)  => { e.target.style.borderColor = T.borderStrong; e.target.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    tabIndex={-1}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', padding: '6px', borderRadius: '6px', color: T.textDim, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {showPw ? (
                      <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </label>

              {/* Remember + Forgot */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    style={{ width: 16, height: 16, borderRadius: 4, accentColor: T.brand }}
                  />
                  <span style={{ fontSize: '14px', color: T.textMuted }}>Manter conectado</span>
                </label>
                <button
                  type="button"
                  style={{ fontSize: '14px', fontWeight: 500, color: T.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = T.brand)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = T.textMuted)}
                >
                  Esqueci a senha
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || isLocked || !email || !password}
                style={{
                  marginTop: '8px',
                  width: '100%',
                  padding: '11px 16px',
                  borderRadius: T.radiusSm,
                  background: isLoading || isLocked || !email || !password ? '#A3C9BB' : T.brand,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '15px',
                  fontFamily: T.font,
                  border: 'none',
                  cursor: isLoading || isLocked ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'background 0.15s',
                  boxShadow: isLoading || isLocked || !email || !password ? 'none' : '0 4px 12px rgba(0,149,95,0.22), 0 12px 32px rgba(0,149,95,0.18)',
                }}
                onMouseEnter={(e) => { if (!isLoading && !isLocked) e.currentTarget.style.background = T.brand600; }}
                onMouseLeave={(e) => { if (!isLoading && !isLocked) e.currentTarget.style.background = T.brand; }}
              >
                {isLoading ? (
                  <>
                    <svg style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Entrando...
                  </>
                ) : isLocked ? (
                  `Aguarde ${lockedRemaining}s`
                ) : (
                  'Entrar'
                )}
              </button>

              {/* TLS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4, fontSize: '12px', color: T.textDim }}>
                <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Conexão criptografada (TLS)
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <footer style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: '12px', color: T.textDim, marginTop: '24px' }}>
          <span>© 2026 PrimeAir · v1.0.0</span>
          <div style={{ display: 'flex', gap: 16 }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: T.textDim, padding: 0 }}>Termos</button>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: T.textDim, padding: 0 }}>Privacidade</button>
          </div>
        </footer>
      </main>
    </div>
  );
}
